import React, { useContext, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  Background,
  Package,
  SeniorImage,
  StudentImage,
} from '../../../models'
import defaultBg from '../../../assets/defaultBg.jpeg'
import classes from './favorite-pose-selection.module.sass'
import FavoritePoseItem from '../../favorite-pose-item'
import Modal from '../../modal'
import PreviewImage from '../../PreviewImage/PreviewImage'
import CloseIcon from '../../svg-icons/close-icon'
import Typography from '../../Typography'
import Button from '../../Button'
import { routes } from '../../../routes'
import { CustomerContext } from '../../../contexts/CustomerContext'

const noImagesContent = (
  <div style={{ padding: '1em' }}>
    <Typography align="center">
      No images are available yet for this student.
      Please check back tomorrow or call
      {' '}
      <a type="tel" href="209-567-0748">209-567-0748</a>
      {' '}
      for more information
    </Typography>
  </div>
)

const FavoritePoseSelection = () => {
  const { customer: { currentStudent } } = useContext(CustomerContext)
  const { packageId } = useParams()
  const history = useHistory()
  const [seniorImages, setSeniorImages] = useState(null)
  const [background, setBackground] = useState(null)
  const [backgrounds, setBackgrounds] = useState(null)
  const [hasNoSeniorImages, setHasNoSeniorImages] = useState(false)
  const [modalImage, setModalImage] = useState(null)
  const [favorites, setFavorites] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const { id: studentId } = currentStudent

  const onFavoriteClick = (id) => {
    if (favorites.includes(id)) setFavorites((prev) => prev.filter((item) => item !== id))
    else setFavorites((prev) => [...prev, id])
  }

  const favoriteAll = () => {
    setFavorites(seniorImages.map(seniorImage => seniorImage.id));
  }

  const onContinue = () => {
    history.push(routes.seniorOptionSelection(packageId, { favs: favorites, bg: background.id }))
  }

  useEffect(() => {
    StudentImage
      .includes('senior_images')
      .where({ studentId, isSenior: true })
      .all()
      .then((res) => {
        if (res.data.length > 0) {
          setIsLoading(false)

          const getImages = () => {
            const correctData = res.data.filter(datum => datum.seniorImages.length > 0);

            return correctData[0].seniorImages.filter((img) => !!img.imageFileName);
          };

          const newImages = getImages();

          setSeniorImages(newImages)
          if (newImages.length === 0) setHasNoSeniorImages(true)
        } else {
          setHasNoSeniorImages(true)
          setIsLoading(false)
        }
      })
  }, [])

  useEffect(() => {
    Package
      .includes({ backgroundSet: ['backgrounds', 'defaultBackground'] })
      .find(packageId)
      .then((res) => {
        const backgroundSet = res.data.backgroundSet

        if (backgroundSet && backgroundSet.backgrounds.length === 0) {
          setBackground(defaultBg)
          return
        }

        setBackground(backgroundSet.defaultBackground)
        setBackgrounds(backgroundSet.backgrounds)
      })
  }, [])

  if (isLoading) return <Typography align="center">Loading your images, please wait...</Typography>
  if (!(seniorImages && background)) return noImagesContent

  const bgUrl = typeof background === 'string' ? background : background.imageUrl;

  return (
    <div className={classes.outer}>
      {!hasNoSeniorImages && (
        <>
          <div className={classes.instructions}>
            <Typography weight="bold" align="center">Select your favorite poses.</Typography>
            <div style={{ fontSize: '1.1em' }}>
              <Typography align="center" weight="bold">(Click to enlarge, scroll for more)</Typography>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '1em 0' }}>
            <Button rounded variant="primary" onClick={favoriteAll}>Favorite All</Button>
          </div>
          <Modal open={!!modalImage}>
            <div className={classes.modalContent}>
              <div className={classes.iconWrapper}>
                <CloseIcon className={classes.closeIcon} onClick={() => setModalImage(null)} />
              </div>
              <PreviewImage foregroundUrl={modalImage} backgroundUrl={bgUrl} />
            </div>
          </Modal>
        </>
      )}
      {hasNoSeniorImages && noImagesContent}
      {!hasNoSeniorImages && (
        <>
          <div
            className={modalImage && window.innerWidth < 768 ? classes.hidden : classes.container}
          >
            {seniorImages.map((seniorImage, i) => {
              const bg = backgrounds ? backgrounds[i % backgrounds.length].imageUrl : bgUrl;

              return (
                <FavoritePoseItem
                  key={seniorImage.id}
                  id={seniorImage.id}
                  fileName={seniorImage.imageFileName}
                  foreground={seniorImage.watermarkUrl}
                  background={bg}
                  alt="senior image"
                  onImageClick={setModalImage}
                  onFavoriteClick={onFavoriteClick}
                  favorited={favorites.includes(seniorImage.id)}
                />
              );
            })}
          </div>
          <div className={classes.continueContainer}>
            <Button variant="primary" disabled={favorites.length === 0} onClick={onContinue}>Continue</Button>
            <Typography align="center">
              Note: Images are not yet retouched.
              Blemishes, shine on skin, green artifacts may still be visible.
              All images will be fully retouched for the yearbook and your portraits.
            </Typography>
          </div>
        </>
      )}
      <div className={classes.hidden}>
        {backgrounds && backgrounds.map((bg) => <img src={bg.imageUrl} key={bg.imageUrl} alt="hidden" />)}
      </div>
    </div>
  )
}

export default FavoritePoseSelection
